import { HandoffMetric } from 'components/onboarding/handoff-hub/handoff-hub.types';
import {
  BaseCriteriaDefinition,
  BaseMetricDefinition,
  ChecklistMetricCriteria,
  ChecklistMetricItem,
  ChecklistMetricStatus,
} from './handoff-checklist-v2.types';

export const mapStatusToResult = (status: ChecklistMetricStatus): string => {
  switch (status) {
    case ChecklistMetricStatus.PASSED:
      return 'pass';
    case ChecklistMetricStatus.WARNING:
      return 'warning';
    case ChecklistMetricStatus.EXCEPTION_NOTED:
      return 'exception_noted';
    case ChecklistMetricStatus.BLOCKED:
    default:
      return 'fail';
  }
};

export const mapResultToStatus = (result: string): ChecklistMetricStatus => {
  switch (result) {
    case 'pass':
      return ChecklistMetricStatus.PASSED;
    case 'warning':
      return ChecklistMetricStatus.WARNING;
    case 'exception_noted':
      return ChecklistMetricStatus.EXCEPTION_NOTED;
    case 'fail':
    default:
      return ChecklistMetricStatus.BLOCKED;
  }
};

// Priority order: BLOCKED > WARNING > EXCEPTION_NOTED > PENDING > PASSED
export const deriveOverallStatus = (
  criteriaStatuses: ChecklistMetricStatus[]
): ChecklistMetricStatus => {
  if (criteriaStatuses.includes(ChecklistMetricStatus.BLOCKED)) {
    return ChecklistMetricStatus.BLOCKED;
  }
  if (criteriaStatuses.includes(ChecklistMetricStatus.WARNING)) {
    return ChecklistMetricStatus.WARNING;
  }
  if (criteriaStatuses.includes(ChecklistMetricStatus.EXCEPTION_NOTED)) {
    return ChecklistMetricStatus.EXCEPTION_NOTED;
  }
  if (criteriaStatuses.includes(ChecklistMetricStatus.PENDING)) {
    return ChecklistMetricStatus.PENDING;
  }
  return ChecklistMetricStatus.PASSED;
};

export const mapChecklistMetricsToHandoffMetrics = (
  checklistMetrics: ChecklistMetricItem[]
): HandoffMetric[] => {
  const handoffMetrics: HandoffMetric[] = [];

  checklistMetrics.forEach((metric) => {
    metric.criteria.forEach((criterion) => {
      handoffMetrics.push({
        section: metric.id,
        subsection: criterion.description,
        actualValue: criterion.reason || criterion.message || '',
        expectedValue: '',
        testCriteria: criterion.description,
        result: mapStatusToResult(criterion.status),
        exception: criterion.exception || '',
      });
    });
  });

  return handoffMetrics;
};

export const mapSnapshotMetricsToChecklist = <TCriteria extends { description: string }>(
  metrics: HandoffMetric[],
  metricDefinitions: BaseMetricDefinition<TCriteria>[],
  resolveCriteria: (
    m: HandoffMetric,
    configCriterion: TCriteria | undefined
  ) => ChecklistMetricCriteria
): ChecklistMetricItem[] => {
  if (!metrics || metrics.length === 0) return [];

  const groupedMap: Record<string, HandoffMetric[]> = {};
  metrics.forEach((m) => {
    if (!groupedMap[m.section]) groupedMap[m.section] = [];
    groupedMap[m.section].push(m);
  });

  return metricDefinitions
    .filter((def) => groupedMap[def.id])
    .map((config) => {
      const criteria = groupedMap[config.id].map((m) => {
        const configCriterion = config.criteria.find(
          (c) => c.description === m.subsection
        );
        return resolveCriteria(m, configCriterion);
      });

      const status = deriveOverallStatus(criteria.map((c) => c.status));

      return {
        id: config.id,
        name: config.name,
        isRequired: config.isRequired !== undefined ? config.isRequired : true,
        allowExceptions:
          config.allowExceptions !== undefined ? config.allowExceptions : false,
        status,
        isLoading: false,
        criteria,
      };
    });
};

/**
 * Initializes checklist metric items in PENDING/loading state from metric definitions,
 * preserving existing exceptions (if any) and resolving whereToFix links.
 */
export const initializeChecklistMetrics = <TContext>(
  definitions: BaseMetricDefinition<BaseCriteriaDefinition<TContext>>[],
  context: TContext,
  existingMetrics: ChecklistMetricItem[] = []
): ChecklistMetricItem[] => {
  return definitions.map((def) => {
    const existingMetric = existingMetrics.find((m) => m.id === def.id);
    return {
      id: def.id,
      name: def.name,
      isRequired: def.isRequired,
      allowExceptions: def.allowExceptions,
      status: ChecklistMetricStatus.PENDING,
      isLoading: true,
      criteria: def.criteria.map((c, idx) => {
        const existingCriterion = existingMetric?.criteria[idx];
        return {
          description: c.description,
          status: ChecklistMetricStatus.PENDING,
          exception: existingCriterion?.exception,
          exceptionType: c.exceptionType,
          exceptionOptions: c.exceptionOptions,
          whereToFix: c.whereToFix
            ? {
                label: c.whereToFix.label,
                url:
                  typeof c.whereToFix.url === 'function'
                    ? c.whereToFix.url(context)
                    : c.whereToFix.url,
              }
            : undefined,
        };
      }),
    };
  });
};

/**
 * Marks every metric and criterion as BLOCKED (not loading) with the given reason.
 * Used when shared data the checklist depends on could not be loaded.
 */
export const blockChecklistMetrics = (
  metrics: ChecklistMetricItem[],
  reason: string
): ChecklistMetricItem[] =>
  metrics.map((m) => ({
    ...m,
    isLoading: false,
    status: ChecklistMetricStatus.BLOCKED,
    criteria: m.criteria.map((c) => ({
      ...c,
      status: ChecklistMetricStatus.BLOCKED,
      reason,
      message: reason,
    })),
  }));

/**
 * Evaluates the criteria of a single metric definition against the context,
 * resolves whereToFix, honors exceptions (mapping BLOCKED to EXCEPTION_NOTED),
 * and derives the overall metric status.
 */
export const evaluateMetricDefinition = async <TContext>(
  def: BaseMetricDefinition<BaseCriteriaDefinition<TContext>>,
  context: TContext,
  existingMetric?: ChecklistMetricItem
): Promise<{
  overallStatus: ChecklistMetricStatus;
  evaluatedCriteria: ChecklistMetricCriteria[];
}> => {
  const criteriaResults = await Promise.allSettled(
    def.criteria.map((c) => c.evaluate(context))
  );

  const evaluatedCriteria: ChecklistMetricCriteria[] = def.criteria.map((c, idx) => {
    const resolvedWhereToFix = c.whereToFix
      ? {
          label: c.whereToFix.label,
          url:
            typeof c.whereToFix.url === 'function'
              ? c.whereToFix.url(context)
              : c.whereToFix.url,
        }
      : undefined;

    const res = criteriaResults[idx];
    const existingCriterion = existingMetric?.criteria[idx];
    const exception = existingCriterion?.exception;

    if (res.status === 'fulfilled') {
      const status =
        res.value.status === ChecklistMetricStatus.BLOCKED && exception?.trim()
          ? ChecklistMetricStatus.EXCEPTION_NOTED
          : res.value.status;

      return {
        description: c.description,
        status,
        whereToFix: resolvedWhereToFix,
        reason: (res.value as { reason?: string }).reason,
        message: res.value.message,
        messageNode: res.value.messageNode,
        actualValue: res.value.actualValue,
        expectedValue: res.value.expectedValue,
        exception,
        exceptionType: c.exceptionType,
        exceptionOptions: c.exceptionOptions,
      };
    } else {
      const status = exception?.trim()
        ? ChecklistMetricStatus.EXCEPTION_NOTED
        : ChecklistMetricStatus.BLOCKED;

      return {
        description: c.description,
        status,
        whereToFix: resolvedWhereToFix,
        reason: res.reason?.message || 'Rejected promise',
        message: 'Failed to evaluate checklist criteria.',
        exception,
        exceptionType: c.exceptionType,
        exceptionOptions: c.exceptionOptions,
      };
    }
  });

  const overallStatus = deriveOverallStatus(evaluatedCriteria.map((c) => c.status));

  return {
    overallStatus,
    evaluatedCriteria,
  };
};
